import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMountEffect } from "primereact/hooks";
import { ProgressSpinner } from "primereact/progressspinner";
import { Field, FormSpy } from "react-final-form";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import Form from "../../common/form";
import Link from "../../common/link";
import {
  TextInput,
  DropdownInput,
  DatePicker,
  TextAreaInput,
} from "../../common/fields";
import * as Validations from "../../common/form/validator";
import { RouteConstants } from "../../../constants";
import EdexLogo from "../../../assets/images/Ed_EX_logo_plain.png";
import {
  configurationRequest,
  registrationRequest,
  resetState,
} from "../../../store/slices/registrationSlice";
import "./signup.scss";

/**
 * Signup Component
 *
 * A component for user registration functionality with two-column layout.
 * Left side shows instructions, right side contains the registration form.
 * This component handles new user registration when self-registration
 * is enabled for the enterprise.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.registration - Registration state from Redux
 * @param {Object} props.global - Global state from Redux
 * @param {Function} props.fetchConfiguration - Action to fetch registration configuration
 * @param {Function} props.registerUser - Action to register a new user
 * @param {Function} props.resetState - Action to reset registration state
 * @returns {JSX.Element} The signup form component
 */
export const SignupForm = ({
  registration,
  global,
  fetchConfiguration,
  registerUser,
  resetState,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  useMountEffect(() => {
    resetState();
    fetchConfiguration();
  });

  // Redirect to signin when registration is successful
  useEffect(() => {
    if (registration.registrationSuccess) {
      // Add a small delay to show the success message before redirecting
      const timer = setTimeout(() => {
        navigate(RouteConstants.APP_ROUTE_LOGIN);
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    }
  }, [registration.registrationSuccess, navigate]);

  // Handler for form submission with reCAPTCHA
  const handleRegistration = async (formData) => {
    // Separate static fields from dynamic fields
    const staticFields = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      username: formData.username,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    };

    // Extract dynamic fields into customFields object
    const customFields = [];
    dynamicFields.forEach((field) => {
      const fieldName = field.name || field.label;
      const fieldId = field.id || field.fieldId;
      if (formData[fieldName] !== undefined && fieldId) {
        customFields.push({ id: fieldId, value: formData[fieldName] || "" });
      }
    });

    // Prepare the registration payload
    let registrationPayload = {
      ...staticFields,
      customFields,
    };

    // Check if reCAPTCHA is enabled and available
    if (registration.configuration?.recaptchaSiteKey && executeRecaptcha) {
      try {
        // Execute reCAPTCHA and get token
        const recaptchaToken = await executeRecaptcha("registration");

        // Add reCAPTCHA token to registration payload
        registrationPayload = {
          ...registrationPayload,
          recaptchaToken,
        };

        registerUser(registrationPayload);
      } catch (error) {
        console.error("reCAPTCHA error:", error);
        // Fallback to registration without reCAPTCHA
        registerUser(registrationPayload);
      }
    } else {
      // Register without reCAPTCHA if not configured or not available
      registerUser(registrationPayload);
    }
  };

  // Parse instructions from configuration
  const getInstructions = () => {
    if (
      !registration.configuration?.selfRegistrationConfiguration?.instructions
    ) {
      return [];
    }

    // Split by newline and clean up whitespace
    return registration.configuration.selfRegistrationConfiguration.instructions
      .split("\n")
      .map((instruction) => instruction.trim())
      .filter((instruction) => instruction.length > 0);
  };

  const instructions = getInstructions();

  // Get dynamic fields from configuration
  const getDynamicFields = () => {
    if (!registration.configuration?.fields) {
      return [];
    }
    return registration.configuration.fields;
  };

  const dynamicFields = getDynamicFields();

  // Email domain validation function
  const validateEmailDomain = (email) => {
    const allowedDomains =
      registration.configuration?.selfRegistrationConfiguration
        ?.allowedEmailDomains;

    // If no domain restriction is set, allow all emails
    if (!allowedDomains) {
      return undefined;
    }

    // If wildcard is set, allow all emails
    if (allowedDomains.trim() === "*") {
      return undefined;
    }

    // Extract domain from email
    const emailDomain = email?.split("@")[1]?.toLowerCase();
    if (!emailDomain) {
      return "Invalid email format";
    }

    // Parse allowed domains (comma-separated)
    const allowedDomainList = allowedDomains
      .split(",")
      .map((domain) => domain.trim().toLowerCase())
      .filter((domain) => domain.length > 0);

    // Check if email domain is in allowed list
    if (!allowedDomainList.includes(emailDomain)) {
      return intl.formatMessage(
        { id: "email_domain_not_allowed" },
        { domains: allowedDomainList.join(", ") }
      );
    }

    return undefined;
  };

  // Get filtered options based on field conditions
  const getFilteredOptions = (field, formValues) => {
    if (!field.options || !field.conditions || field.conditions.length === 0) {
      return field.options || [];
    }

    // Get all valid option keys based on conditions
    const validOptionKeys = new Set();

    field.conditions.forEach((condition) => {
      // Find the dependent field to get its actual form field name
      const dependentField = dynamicFields.find(
        (f) => f.id === condition.dependentFieldId
      );
      const dependentFieldName = dependentField
        ? dependentField.name || dependentField.label
        : condition.dependentFieldId;

      const dependentFieldValue =
        formValues[dependentFieldName] ?? dependentField.defaultValue;

      if (dependentFieldValue === condition.expectedValue) {
        // Split optionKey by comma and add each key
        const optionKeys = condition.optionKey
          .split(",")
          .map((key) => key.trim());
        optionKeys.forEach((key) => validOptionKeys.add(key));
      }
    });

    // If no conditions are met, show all options
    if (validOptionKeys.size === 0) {
      return field.options || [];
    }

    // Filter options based on valid keys
    return (field.options || []).filter((option) => {
      const optionKey = option.key || option.value || option.name || option;

      // For boolean fields, map generic keys to actual labels
      if (
        field.type.toLowerCase() === "boolean" &&
        field.options &&
        field.options.length === 2
      ) {
        const [yesLabel, noLabel] = field.options;

        // Check if validOptionKeys contains generic boolean keys and map them
        for (const validKey of validOptionKeys) {
          if (
            (validKey.toLowerCase() === "yes" && option === yesLabel) ||
            (validKey.toLowerCase() === "no" && option === noLabel) ||
            validKey === option
          ) {
            return true;
          }
        }
        return false;
      }

      // For other field types (dropdown), use direct key matching
      return validOptionKeys.has(optionKey);
    });
  };

  // Component to handle username auto-population
  const UsernameAutoPopulator = ({ email, form }) => {
    useEffect(() => {
      if (!email) return;

      const usernameFieldState = form.getFieldState("username");
      const currentUsername = form.getState().values.username;

      // Only auto-populate if:
      // 1. Email exists
      // 2. Username field hasn't been touched/modified by user
      // 3. Current username is empty or same as email (to allow updates)
      if (
        !usernameFieldState?.touched ||
        !currentUsername ||
        currentUsername === email
      ) {
        form.change("username", email);
      }
    }, [email]);
    return null;
  };

  // Render form fields - reusable for both layouts
  const renderFormFields = (formValues = {}) => {
    return (
      <>
        <div className="col-12 md:col-6">
          <div className="field">
            <Field
              name="firstName"
              component={TextInput}
              label={intl.formatMessage({ id: "first_name" })}
              placeholder={intl.formatMessage({ id: "first_name" })}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <Field
              name="lastName"
              component={TextInput}
              label={intl.formatMessage({ id: "last_name" })}
              placeholder={intl.formatMessage({ id: "last_name" })}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <Field
              name="email"
              component={TextInput}
              label={intl.formatMessage({ id: "email" })}
              placeholder={intl.formatMessage({ id: "email" })}
              type="email"
              icon="pi-envelope"
              required
              validate={Validations.composeValidators(
                Validations.required,
                Validations.checkEmailFormat,
                validateEmailDomain
              )}
            />
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <Field
              name="username"
              component={TextInput}
              label={intl.formatMessage({ id: "username" })}
              placeholder={intl.formatMessage({ id: "username" })}
              icon="pi-user"
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <Field
              name="password"
              component={TextInput}
              label={intl.formatMessage({ id: "password" })}
              placeholder={intl.formatMessage({ id: "password" })}
              type="password"
              required
              infoMessage={"password_info"}
              validate={Validations.composeValidators(
                Validations.required,
                Validations.validateStrongPassword
              )}
            />
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <Field
              name="confirmPassword"
              component={TextInput}
              label={intl.formatMessage({
                id: "confirm_password",
              })}
              placeholder={intl.formatMessage({ id: "confirm_password" })}
              type="password"
              required
              validate={Validations.composeValidators(
                Validations.required,
                Validations.mustMatch(
                  "password",
                  intl.formatMessage({ id: "passwords_must_match" })
                )
              )}
            />
          </div>
        </div>

        {/* Dynamic Fields */}
        {dynamicFields.length > 0 && (
          <>
            {dynamicFields.map((field) => {
              // Determine the component based on field type
              let FieldComponent = TextInput;
              let fieldProps = {
                type: field.type || "text",
              };

              if (
                field.type.toLowerCase() === "dropdown" ||
                field.type.toLowerCase() === "boolean"
              ) {
                FieldComponent = DropdownInput;

                // Get filtered options based on conditions
                const filteredOptions = getFilteredOptions(field, formValues);

                fieldProps = {
                  options: filteredOptions
                    ? filteredOptions.map((option, index) => ({
                        id: option.id || option.value || index,
                        name:
                          option.name || option.label || option.value || option,
                        value: option.value || option.name || option,
                        i18n: option.i18n || false,
                      }))
                    : [],
                  editable: field.editable || false,
                };
              } else if (field.type.toLowerCase() === "date") {
                FieldComponent = DatePicker;
                // Past dates (for example a date of birth): no minimum, up to today
                fieldProps = {
                  minDate: null,
                  maxDate: new Date(),
                };
              } else if (
                field.type === 6 ||
                field.type.toLowerCase() === "textlong"
              ) {
                // TextLong field type
                FieldComponent = TextAreaInput;
                fieldProps = {
                  rows: 4, // Default rows for textarea
                  maxLength: field.maxLength || 500, // Use field's maxLength or default to 500
                  placeholder:
                    field.placeholder ||
                    (field.i18n
                      ? intl.formatMessage({ id: field.label })
                      : field.label),
                };
              }

              // Determine grid column class based on field type
              const columnClass =
                field.type === 6 || field.type.toLowerCase() === "textlong"
                  ? "col-12" // TextLong fields take full width
                  : "col-12 md:col-6"; // Other fields take half width on medium+ screens

              return (
                <div key={field.name || field.label} className={columnClass}>
                  <div className="field">
                    <Field
                      name={field.name || field.label}
                      component={FieldComponent}
                      label={
                        field.i18n
                          ? intl.formatMessage({ id: field.label })
                          : field.label
                      }
                      placeholder={
                        // For TextLong, use the specific placeholder from fieldProps if set
                        (field.type === 6 ||
                          field.type.toLowerCase() === "textlong") &&
                        fieldProps.placeholder
                          ? fieldProps.placeholder
                          : field.i18n
                          ? intl.formatMessage({ id: field.label })
                          : field.label
                      }
                      initialValue={field.defaultValue}
                      required={field.isRequired}
                      readOnly={field.readOnly}
                      validate={
                        field.isRequired
                          ? Validations.composeValidators(Validations.required)
                          : undefined
                      }
                      {...fieldProps}
                    />
                  </div>
                </div>
              );
            })}
          </>
        )}
      </>
    );
  };

  // Get logo source based on enterprise configuration
  const getLogoSrc = () => {
    if (global.enterprise && global.enterprise.logo) {
      return `${new URL(
        global.enterprise.logo,
        process.env.REACT_APP_URL
      ).toString()}`;
    }
    return EdexLogo;
  };

  return (
    <div className="signup-container">
      {/* Logo Section */}
      <div className="signup-logo flex flex-column align-items-center mb-4">
        <img
          src={getLogoSrc()}
          alt="Logo"
          className="logo"
          style={{ maxHeight: "80px", marginBottom: "2rem" }}
        />
      </div>

      {/* Main Content */}
      <div className="signup-content">
        {registration.requesting &&
        registration.action === "fetch-configuration" ? (
          /* Show only loading spinner when fetching configuration */
          <div className="text-center">
            <ProgressSpinner />
          </div>
        ) : (
          /* Show form and instructions after configuration is loaded */
          <div className="grid">
            {/* Left Side - Instructions (only show if instructions exist) */}
            {instructions.length > 0 && (
              <div className="col-12 md:col-4">
                <div className="registration-instructions">
                  <h3 className="text-xl font-semibold mb-3">
                    {intl.formatMessage({ id: "registration_instructions" })}
                  </h3>

                  <div className="registration-instructions-container">
                    <ul className="list-disc pl-4 registration-instructions-list">
                      {instructions.map((instruction, index) => (
                        <li key={index} className="mb-2">
                          {instruction.charAt(0).toUpperCase() +
                            instruction.slice(1).toLowerCase()}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Right Side - Registration Form */}
            <div
              className={instructions.length > 0 ? "col-12 md:col-8" : "col-12"}
            >
              <div className="registration-form-container">
                <div className="title text-center mb-4">
                  {intl.formatMessage({ id: "sign_up" })}
                </div>

                <Form
                  initialValues={{}}
                  fullWidthButtons
                  requesting={
                    registration.requesting &&
                    registration.action === "register-user"
                  }
                  submitBtn={{
                    label: intl.formatMessage({ id: "create_account" }),
                    icon: "pi-user-plus",
                    onClick: handleRegistration,
                  }}
                >
                  <div className="grid">
                    <FormSpy subscription={{ values: true }}>
                      {({ values, form }) => (
                        <>
                          <UsernameAutoPopulator
                            email={values.email}
                            form={form}
                          />
                          {renderFormFields(values)}
                        </>
                      )}
                    </FormSpy>
                  </div>
                </Form>

                {/* Back to Login Link */}
                <div className="text-center mt-2">
                  <Link to={RouteConstants.APP_ROUTE_LOGIN}>
                    {intl.formatMessage({ id: "back_to_login" })}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Powered By Footer */}
      <div className="signup-footer">
        <div className="footer flex flex-row justify-content-center w-full mb-3 mt-3">
          <div className="powered-by flex flex-row align-items-center gap-2">
            <span className="text-muted">Powered by</span>
            <img src={EdexLogo} alt="Powered By" className="powered-by-logo" />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  registration: state.registration,
  global: state.global,
});

const mapDispatchToProps = (dispatch) => ({
  fetchConfiguration: () => dispatch(configurationRequest()),
  registerUser: (data) => dispatch(registrationRequest(data)),
  resetState: () => dispatch(resetState()),
});

const ConnectedSignupForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(SignupForm);

// Main Signup component with reCAPTCHA provider
const Signup = (props) => {
  const { registration } = props;
  const recaptchaSiteKey = registration.configuration?.recaptchaSiteKey;

  // If no site key is available yet, render without reCAPTCHA provider
  if (!recaptchaSiteKey) {
    return <ConnectedSignupForm {...props} />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <ConnectedSignupForm {...props} />
    </GoogleReCaptchaProvider>
  );
};

export default connect(mapStateToProps)(Signup);
